import React, { useState, useEffect } from 'react';
import { awsService } from '../../services/awsService';
import EKSDetailsView from './EKSDetailsView';
import {
  ServerIcon,
  CircleStackIcon,
  FolderIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

// Custom icons
const EC2Icon = ({ className }: { className?: string }) => (
  <img src="/ec2.svg" alt="EC2" className={className} />
);

const EBSIcon = ({ className }: { className?: string }) => (
  <img src="/ebs.svg" alt="EBS" className={className} />
);

const S3BucketIcon = ({ className }: { className?: string }) => (
  <img src="/s3-bucket.svg" alt="S3" className={className} />
);

const EKSIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M9 12L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M9 12L15 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const ECSIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="2"/>
    <rect x="3" y="9" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="2"/>
    <rect x="3" y="15" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="2"/>
    <circle cx="6" cy="6" r="1" fill="currentColor"/>
    <circle cx="6" cy="12" r="1" fill="currentColor"/>
    <circle cx="6" cy="18" r="1" fill="currentColor"/>
  </svg>
);

const RDSIcon = ({ className }: { className?: string }) => (
  <img src="/rds.svg" alt="RDS" className={className} />
);

// Custom CloudFront Icon (Global network/CDN icon)
const CloudFrontIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
    <path d="M3.5 9H20.5" stroke="currentColor" strokeWidth="2"/>
    <path d="M3.5 15H20.5" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 3C12 3 8 6 8 12C8 18 12 21 12 21" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 3C12 3 16 6 16 12C16 18 12 21 12 21" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

// Custom VPC Icon (Network icon)
const VPCIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="8" width="18" height="8" rx="1" stroke="currentColor" strokeWidth="2"/>
    <circle cx="8" cy="12" r="2" fill="currentColor"/>
    <circle cx="16" cy="12" r="2" fill="currentColor"/>
    <path d="M10 12H14" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 4V8" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 16V20" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

// Custom Lambda Icon (λ symbol)
const LambdaIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 19L10 5L12 12L14 5L19 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 14H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

interface ServiceDetailsViewProps {
  onCostUpdate?: (totalCost: number) => void;
}

interface GroupedEC2Instance {
  instanceType: string;
  count: number;
  vcpus: number;
  memory: number;
  regions: string[];
  totalMonthlyCost: number;
  runningCount: number;
}

interface GroupedEBSVolume {
  volumeType: string;
  count: number;
  totalSizeGiB: number;
  regions: string[];
  totalMonthlyCost: number;
}

const ServiceDetailsView: React.FC<ServiceDetailsViewProps> = ({ onCostUpdate }) => {
  const [activeTab, setActiveTab] = useState('ec2');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // State for each service
  const [ec2Data, setEc2Data] = useState<any>(null);
  const [ebsData, setEbsData] = useState<any>(null);
  const [s3Data, setS3Data] = useState<any>(null);
  const [eksData, setEksData] = useState<any>(null);
  const [ecsData, setEcsData] = useState<any>(null);
  const [rdsData, setRdsData] = useState<any>(null);
  const [fsxData, setFsxData] = useState<any>(null);
  const [efsData, setEfsData] = useState<any>(null);
  const [dynamoData, setDynamoData] = useState<any>(null);
  const [logsData, setLogsData] = useState<any>(null);
  const [cloudFrontData, setCloudFrontData] = useState<any>(null);
  const [vpcData, setVpcData] = useState<any>(null);
  const [lambdaData, setLambdaData] = useState<any>(null);

  useEffect(() => {
    loadServiceData(activeTab);
    setCurrentPage(1);
  }, [activeTab]);

  const loadServiceData = async (service: string) => {
    setLoading(true);
    setError('');
    
    try {
      switch (service) {
        case 'ec2':
          const ec2 = await awsService.getEC2Instances();
          setEc2Data(ec2);
          break;
        case 'ebs':
          const ebs = await awsService.getEBSVolumes();
          setEbsData(ebs);
          break;
        case 's3':
          const s3 = await awsService.getS3Buckets();
          setS3Data(s3);
          break;
        case 'eks':
          const eks = await awsService.getEKSClusters();
          setEksData(eks);
          break;
        case 'ecs':
          const ecs = await awsService.getECSClusters();
          setEcsData(ecs);
          break;
        case 'rds':
          const rds = await awsService.getRDSInstances();
          setRdsData(rds);
          break;
        case 'fsx':
          const fsx = await awsService.getFSxFileSystems();
          setFsxData(fsx);
          break;
        case 'efs':
          const efs = await awsService.getEFSFileSystems();
          setEfsData(efs);
          break;
        case 'dynamodb':
          const dynamo = await awsService.getDynamoDBTables();
          setDynamoData(dynamo);
          break;
        case 'cloudwatch':
          const logs = await awsService.getCloudWatchLogGroups();
          setLogsData(logs);
          break;
        case 'cloudfront':
          const cloudfront = await awsService.getCloudFrontDistributions();
          setCloudFrontData(cloudfront);
          break;
        case 'vpc':
          const vpc = await awsService.getVPCs();
          setVpcData(vpc);
          break;
        case 'lambda':
          const lambda = await awsService.getLambdaFunctions();
          setLambdaData(lambda);
          break;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load service data');
    } finally {
      setLoading(false);
    }
  };

  // Organized tabs by category
  const tabCategories = [
    {
      name: 'Compute',
      tabs: [
        { id: 'ec2', name: 'EC2', icon: EC2Icon },
        { id: 'ecs', name: 'ECS', icon: ECSIcon },
        { id: 'eks', name: 'EKS', icon: EKSIcon },
      ]
    },
    {
      name: 'Database',
      tabs: [
        { id: 'dynamodb', name: 'DynamoDB', icon: CircleStackIcon },
        { id: 'rds', name: 'RDS', icon: RDSIcon },
      ]
    },
    {
      name: 'Storage',
      tabs: [
        { id: 'ebs', name: 'EBS', icon: EBSIcon },
        { id: 'efs', name: 'EFS', icon: FolderIcon },
        { id: 'fsx', name: 'FSx', icon: ServerIcon },
        { id: 's3', name: 'S3', icon: S3BucketIcon },
      ]
    },
    {
      name: 'CDN & Networking',
      tabs: [
        { id: 'cloudfront', name: 'CloudFront', icon: CloudFrontIcon },
        { id: 'vpc', name: 'VPC', icon: VPCIcon },
      ]
    },
    {
      name: 'Other Services',
      tabs: [
        { id: 'cloudwatch', name: 'CloudWatch', icon: DocumentTextIcon },
        { id: 'lambda', name: 'Lambda', icon: LambdaIcon },
      ]
    },
  ];

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const calculateTotalNodes = (cluster: any): number => {
    let totalNodes = 0;
    
    // Count managed node groups
    if (cluster.nodeGroups && cluster.nodeGroups.length > 0) {
      cluster.nodeGroups.forEach((nodeGroup: any) => {
        // Parse scalingConfig if it's a string
        const scalingConfig = typeof nodeGroup.scalingConfig === 'string' 
          ? JSON.parse(nodeGroup.scalingConfig) 
          : nodeGroup.scalingConfig;
        
        // Use the desiredSize from scalingConfig if available
        if (scalingConfig?.desiredSize) {
          totalNodes += Number(scalingConfig.desiredSize);
        } else if (scalingConfig?.minSize) {
          // Fallback to minSize if desiredSize not available
          totalNodes += Number(scalingConfig.minSize);
        }
      });
    }
    
    // For self-managed nodes, we need to count EC2 instances
    // Since we don't have access to EC2 data here, we'll show the managed nodes count
    // The total count including self-managed nodes is shown in the Resource Summary
    return totalNodes || cluster.totalNodes || 0;
  };

  const groupEC2Instances = (instances: any[]): GroupedEC2Instance[] => {
    const grouped: { [key: string]: GroupedEC2Instance } = {};
    
    // Filter out instances that belong to EKS clusters
    const nonEKSInstances = instances.filter(instance => {
      const eksClusterTag = instance.tags?.['aws:eks:cluster-name'];
      return !eksClusterTag;
    });
    
    nonEKSInstances.forEach(instance => {
      const key = instance.instanceType;
      if (!grouped[key]) {
        grouped[key] = {
          instanceType: instance.instanceType,
          count: 0,
          vcpus: instance.vcpus || 0,
          memory: instance.memory || 0,
          regions: [],
          totalMonthlyCost: 0,
          runningCount: 0
        };
      }
      
      grouped[key].count++;
      if (instance.state === 'running') {
        grouped[key].runningCount++;
        // Estimate monthly cost (720 hours)
        grouped[key].totalMonthlyCost += (instance.onDemandPricePerHour || 0) * 720;
      }
      
      if (!grouped[key].regions.includes(instance.region)) {
        grouped[key].regions.push(instance.region);
      }
    });
    
    return Object.values(grouped);
  };

  const groupEBSVolumes = (volumes: any[]): GroupedEBSVolume[] => {
    const grouped: { [key: string]: GroupedEBSVolume } = {};
    
    volumes.forEach(volume => {
      const key = volume.volumeType;
      if (!grouped[key]) {
        grouped[key] = {
          volumeType: volume.volumeType,
          count: 0,
          totalSizeGiB: 0,
          regions: [],
          totalMonthlyCost: 0
        };
      }
      
      grouped[key].count++;
      grouped[key].totalSizeGiB += volume.size || 0;
      grouped[key].totalMonthlyCost += volume.monthlyStorageCost || 0;
      
      if (!grouped[key].regions.includes(volume.region)) {
        grouped[key].regions.push(volume.region);
      }
    });
    
    return Object.values(grouped);
  };

  const renderEC2 = () => {
    if (!ec2Data) return null;
    
    const { instances } = ec2Data;
    const groupedInstances = groupEC2Instances(instances || []);
    
    // Pagination
    const totalPages = Math.ceil(groupedInstances.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = groupedInstances.slice(startIndex, endIndex);
    
    return (
      <div>
        <div className="overflow-hidden rounded-md bg-gray-50 dark:bg-gray-900/50">
          <table className="min-w-full">
            <thead className="bg-gray-100 dark:bg-gray-600/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Instance Type
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Count
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  vCPUs
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Memory
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Regions
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Monthly Cost
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
              {currentItems.map((group, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {group.instanceType}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {group.count} ({group.runningCount} running)
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {group.vcpus}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {group.memory} GB
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="truncate block max-w-xs" title={group.regions.join(', ')}>
                      {group.regions.join(', ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                    ${(Number(group.totalMonthlyCost) || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-b-md">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, groupedInstances.length)}</span> of{' '}
                  <span className="font-medium">{groupedInstances.length}</span> instance types
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEBS = () => {
    if (!ebsData) return null;
    
    const { volumes } = ebsData;
    const groupedVolumes = groupEBSVolumes(volumes || []);
    
    return (
      <div className="overflow-hidden rounded-md bg-gray-50 dark:bg-gray-900/50">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gray-600/50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Volume Type
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Count
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Total Size
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Regions
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Monthly Cost
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
            {groupedVolumes.map((group, index) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {group.volumeType}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {group.count}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {group.totalSizeGiB} GB
                </td>
                <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  <span className="truncate block max-w-xs" title={group.regions.join(', ')}>
                    {group.regions.join(', ')}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                  ${(Number(group.totalMonthlyCost) || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderS3 = () => {
    if (!s3Data) return null;
    
    const { buckets } = s3Data;
    
    // Pagination
    const totalPages = Math.ceil((buckets?.length || 0) / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = buckets?.slice(startIndex, endIndex) || [];
    
    return (
      <div>
        <div className="overflow-hidden rounded-md bg-gray-50 dark:bg-gray-900/50">
          <table className="min-w-full">
            <thead className="bg-gray-100 dark:bg-gray-600/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Bucket Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Region
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Objects
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Monthly Cost
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
              {currentItems.map((bucket: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                    <span className="truncate block max-w-xs" title={bucket.bucketName}>
                      {bucket.bucketName}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {bucket.region}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatBytes(bucket.totalSizeBytes)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {(bucket.totalObjects || 0).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                    ${(Number(bucket.monthlyCost) || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && renderPagination(buckets?.length || 0, totalPages, startIndex, endIndex)}
      </div>
    );
  };

  const renderEKS = () => {
    if (!eksData || !eksData.length) return <div className="text-center py-4 text-gray-500">No EKS clusters found</div>;
    return <EKSDetailsView eksData={eksData} ec2Instances={ec2Data?.instances} />;
  };

  const renderRDS = () => {
    if (!rdsData) return null;
    
    const { instances } = rdsData;
    
    return (
      <div className="overflow-hidden rounded-md bg-gray-50 dark:bg-gray-900/50">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gray-600/50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Instance ID
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Engine
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Class
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Storage
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Region
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Monthly Cost
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
            {instances?.map((instance: any, index: number) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                  <span className="truncate block max-w-xs" title={instance.dbInstanceIdentifier}>
                    {instance.dbInstanceIdentifier}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {instance.engine} {instance.engineVersion}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {instance.dbInstanceClass}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {instance.allocatedStorage} GB ({instance.storageType})
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {instance.region}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                  ${(Number(instance.monthlyComputeCost || 0) + Number(instance.monthlyStorageCost || 0)).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagination = (totalItems: number, totalPages: number, startIndex: number, endIndex: number) => (
    <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-b-md">
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
            <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{' '}
            <span className="font-medium">{totalItems}</span> results
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'ec2':
        return renderEC2();
      case 'ecs':
        return renderECS();
      case 'ebs':
        return renderEBS();
      case 's3':
        return renderS3();
      case 'eks':
        return renderEKS();
      case 'rds':
        return renderRDS();
      case 'fsx':
        return renderFSx();
      case 'efs':
        return renderEFS();
      case 'dynamodb':
        return renderDynamoDB();
      case 'cloudwatch':
        return renderCloudWatchLogs();
      case 'cloudfront':
        return renderCloudFront();
      case 'vpc':
        return renderVPC();
      case 'lambda':
        return renderLambda();
      default:
        return null;
    }
  };

  const renderECS = () => {
    if (!ecsData) return null;
    
    const { clusters, services, taskDefinitions } = ecsData;
    
    if (!clusters || clusters.length === 0) {
      return <div className="text-center py-4 text-gray-500">No ECS clusters found</div>;
    }
    
    return (
      <div className="space-y-6">
        {/* Clusters */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Clusters</h3>
          <div className="bg-gray-50 dark:bg-gray-900/50 overflow-hidden rounded-md">
            <table className="min-w-full">
              <thead className="bg-gray-100 dark:bg-gray-600/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Cluster Name
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Services
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Running Tasks
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Region
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
                {clusters.map((cluster: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                      {cluster.clusterName}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        cluster.status === 'ACTIVE' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {cluster.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cluster.activeServicesCount}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cluster.runningTasksCount}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cluster.region}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Services */}
        {services && services.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Services</h3>
            <div className="overflow-hidden rounded-md bg-gray-50 dark:bg-gray-900/50">
              <table className="min-w-full">
                <thead className="bg-gray-100 dark:bg-gray-600/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Service Name
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Launch Type
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Running/Desired
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Monthly Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
                  {services.slice(0, 10).map((service: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                        {service.serviceName}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          service.status === 'ACTIVE' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {service.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {service.launchType || 'EC2'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {service.runningCount}/{service.desiredCount}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                        ${(Number(service.monthlyCost) || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFSx = () => {
    if (!fsxData) return null;
    
    const { fileSystems } = fsxData;
    
    return (
      <div className="overflow-hidden rounded-md bg-gray-50 dark:bg-gray-900/50">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gray-600/50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                File System ID
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Type
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Storage
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Region
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Monthly Cost
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
            {fileSystems?.map((fs: any, index: number) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                  {fs.fileSystemId}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {fs.fileSystemType}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {fs.storageCapacity} GB
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {fs.region}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {fs.lifecycleState}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                  ${(Number(fs.monthlyCost) || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderEFS = () => {
    if (!efsData) return null;
    
    const { fileSystems } = efsData;
    
    return (
      <div className="overflow-hidden rounded-md bg-gray-50 dark:bg-gray-900/50">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gray-600/50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Name / ID
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Size
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Performance
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Mount Targets
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Region
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Monthly Cost
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
            {fileSystems?.map((fs: any, index: number) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                  {fs.name || fs.fileSystemId}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatBytes(fs.sizeInBytes)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {fs.performanceMode}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {fs.numberOfMountTargets}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {fs.region}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                  ${(Number(fs.monthlyCost) || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDynamoDB = () => {
    if (!dynamoData) return null;
    
    const { tables } = dynamoData;
    
    return (
      <div className="overflow-hidden rounded-md bg-gray-50 dark:bg-gray-900/50">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gray-600/50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Table Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Billing Mode
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Items
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Size
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Region
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Monthly Cost
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
            {tables?.map((table: any, index: number) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                  <span className="truncate block max-w-xs" title={table.tableName}>
                    {table.tableName}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {table.billingMode}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {table.itemCount.toLocaleString()}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatBytes(table.sizeBytes)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {table.region}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                  ${(Number(table.monthlyCost) || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCloudWatchLogs = () => {
    if (!logsData) return null;
    
    const { logGroups } = logsData;
    
    return (
      <div className="overflow-hidden rounded-md bg-gray-50 dark:bg-gray-900/50">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gray-600/50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Log Group Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Stored Size
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Monthly Incoming
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Region
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Monthly Cost
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
            {logGroups?.map((log: any, index: number) => (
              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                  <span className="truncate block max-w-xs" title={log.logGroupName}>
                    {log.logGroupName}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatBytes(log.storedBytes)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {(Number(log.monthlyIncomingGB) || 0).toFixed(2)} GB
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {log.region}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                  ${(Number(log.monthlyCost) || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCloudFront = () => {
    if (!cloudFrontData) return null;
    
    const { distributions } = cloudFrontData;
    
    if (!distributions || distributions.length === 0) {
      return <div className="text-center py-4 text-gray-500">No CloudFront distributions found</div>;
    }
    
    // Pagination
    const totalPages = Math.ceil(distributions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = distributions.slice(startIndex, endIndex);
    
    return (
      <div>
        <div className="bg-gray-50 dark:bg-gray-900/50 overflow-hidden rounded-md">
          <table className="min-w-full">
            <thead className="bg-gray-100 dark:bg-gray-600/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Distribution ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Domain Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Price Class
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Monthly Transfer
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Monthly Requests
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Monthly Cost
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
              {currentItems.map((distribution: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                    <span className="truncate block max-w-xs" title={distribution.distributionId}>
                      {distribution.distributionId}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="truncate block max-w-xs" title={distribution.domainName}>
                      {distribution.domainName}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      distribution.enabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    }`}>
                      {distribution.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {distribution.priceClass}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {(Number(distribution.monthlyDataTransferGB) || 0).toFixed(2)} GB
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {((distribution.monthlyHttpRequests || 0) + (distribution.monthlyHttpsRequests || 0)).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                    ${(Number(distribution.totalMonthlyCost) || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && renderPagination(distributions.length, totalPages, startIndex, endIndex)}
      </div>
    );
  };

  const renderVPC = () => {
    if (!vpcData) return null;
    
    const { vpcs } = vpcData;
    
    if (!vpcs || vpcs.length === 0) {
      return <div className="text-center py-4 text-gray-500">No VPCs found</div>;
    }
    
    // Pagination
    const totalPages = Math.ceil(vpcs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = vpcs.slice(startIndex, endIndex);
    
    return (
      <div>
        <div className="bg-gray-50 dark:bg-gray-900/50 overflow-hidden rounded-md">
          <table className="min-w-full">
            <thead className="bg-gray-100 dark:bg-gray-600/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  VPC ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  CIDR Block
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Subnets
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  NAT Gateways
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  VPN Connections
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Region
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Monthly Cost
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
              {currentItems.map((vpc: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                    {vpc.vpcId}
                    {vpc.isDefault && (
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Default)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    {vpc.name || '-'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {vpc.cidrBlock}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {vpc.subnetCount}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {vpc.natGatewayCount}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {vpc.vpnConnectionCount}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {vpc.region}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                    ${(Number(vpc.monthlyCost) || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && renderPagination(vpcs.length, totalPages, startIndex, endIndex)}
      </div>
    );
  };

  const renderLambda = () => {
    if (!lambdaData) return null;
    
    const { functions } = lambdaData;
    
    if (!functions || functions.length === 0) {
      return <div className="text-center py-4 text-gray-500">No Lambda functions found</div>;
    }
    
    // Pagination
    const totalPages = Math.ceil(functions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = functions.slice(startIndex, endIndex);
    
    return (
      <div>
        <div className="bg-gray-50 dark:bg-gray-900/50 overflow-hidden rounded-md">
          <table className="min-w-full">
            <thead className="bg-gray-100 dark:bg-gray-600/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Function Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Runtime
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Memory
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Code Size
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Invocations
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Avg Duration
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Region
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Monthly Cost
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-700/50">
              {currentItems.map((func: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
                    <span className="truncate block max-w-xs" title={func.functionName}>
                      {func.functionName}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {func.runtime}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {func.memorySize} MB
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatBytes(func.codeSize)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {func.invocations.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {(Number(func.averageDuration) || 0).toFixed(0)} ms
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {func.region}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                    ${(Number(func.monthlyCost) || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && renderPagination(functions.length, totalPages, startIndex, endIndex)}
      </div>
    );
  };

  return (
    <div className="mt-8 mb-8">
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
        <div className="px-6 pt-4 pb-2">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Service Details
          </h2>
        </div>
        
        <div className="px-6 pb-6">
          {/* Tabs organized by category */}
          <div className="mb-6">
            <div className="flex space-x-8">
              {tabCategories.map((category) => (
                <div key={category.name} className="flex flex-col">
                  <div className="mb-1">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {category.name}
                    </span>
                  </div>
                  <div className="border-b border-gray-300 dark:border-gray-600 mb-3 w-full"></div>
                  <div className="flex space-x-1">
                    {category.tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                          px-3 py-2 text-sm font-medium rounded-md transition-colors
                          ${activeTab === tab.id
                            ? 'bg-slate-900 text-white'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                          }
                        `}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Content */}
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            ) : (
              renderContent()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailsView;